import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  ViewChild,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxMasonryComponent, NgxMasonryModule } from 'ngx-masonry';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReusableService } from '@services/reusable.service';
import { EditableCardComponent } from '@components/cards/editable-card/editable-card.component';
import { AlertComponent, ButtonComponent, ImageComponent } from '@mds/ui';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { MatDialog } from '@angular/material/dialog';
import { ActionItem, ModalActionEnum } from '@dialogs/actions-panel/actions-panel.component';
import { AttributeSearchDialogComponent } from '@dialogs/attribute-search-dialog/attribute-search-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-description',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    AlertComponent,
    MatTooltipModule,
    EditableCardComponent,
    ImageComponent,
    MdsIconsModule,
    NgxMasonryModule
  ],
  templateUrl: './description.component.html',
  styleUrls: ['./description.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DescriptionComponent implements OnChanges, OnDestroy {
  @Input() data: any;
  @Input() isDrawerOpen: boolean;
  @ViewChild(NgxMasonryComponent) masonry: NgxMasonryComponent;
  @Output() reloadData = new EventEmitter<any>();
  sectionData: any[] = [];
  actions: any;
  forecastSourceActions: object;
  forecastSourceName = 'Forecast Source';
  productInfo: any;
  targetRid: any;
  @Input() productImage: string;
  @Input() isForecastScenario = false;
  protected readonly MdsIcon = MdsIcon;
  protected isChildPCR = false;
  protected warningText: string | null;
  protected actionWarningText: string | null;
  private dialog = inject(MatDialog);
  private reusableService = inject(ReusableService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  @Output() actionToPerform = new EventEmitter<ActionItem[]>();

  ngOnChanges(): void {
    this.init();
  }

  reloadMasonryLayout(): void {
    if (this.masonry) {
      this.masonry.reloadItems();
      this.masonry.layout();
    }
  }

  reloadCardData() {
    this.reloadData.emit();
  }

  ngOnDestroy(): void {
    this.dialog.closeAll();
  }

  private init() {
    this.actionWarningText = this.data?.actionWarning?.text ?? null;
    if (!this.data) return;
    this.route.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((segments) => {
      const currentPath = segments.map((s) => s.path).join('/');
      if (currentPath.includes('explorer')) {
        this.data.layer = 'explorer';
      }
    });
    if (this.data.actions) {
      this.actions = this.data.actions;
      this.forecastSourceActions = {
        ...this.data.actions,
        edit: false,
        delete: false
      };
    }
    const sections = this.data?.sections;
    this.productInfo = this.data.productInfo;
    this.targetRid = this.data.targetRid;
    this.reusableService.pradicateBy(sections, 'sortlevel');
    sections.forEach((e) => {
      e.productInfo = this.productInfo;
      e.targetRid = this.targetRid;
    });
    this.sectionData = sections;
    this.getDefaultManufacturingProcess();
    this.checkPCRRelation();
    this.activateActionModal();
  }

  private getDefaultManufacturingProcess(): void {
    const defaultProcess = this.sectionData.find((e) => e.name === 'Default Manufacturing Process');
    const allProcess = this.sectionData.find((e) => e.name === 'Manufacturing processes');
    if (defaultProcess && allProcess) {
      allProcess.defaultRid = defaultProcess.lines[0]?.value;
      defaultProcess.isCardHidden = true;
    }
  }

  private checkPCRRelation() {
    const relationshipSection = this.sectionData.find((section) => section.name === 'Relationships');
    if (relationshipSection?.lines?.length) {
      this.isChildPCR = true;
      this.warningText = 'PCR has a parent and may extend or override the parent’s rules.';
    }
  }

  private activateActionModal(): void {
    const ADD_ACTION: ActionItem = {
      label: 'Add Attributes',
      icon: MdsIcon.PLUS_DARK,
      value: ModalActionEnum.ADD,
      callback: () => this.handleAddAttributeAction()
    };

    this.actionToPerform.emit([ADD_ACTION]);
  }

  private handleAddAttributeAction() {
    this.dialog.open(AttributeSearchDialogComponent, {
      data: {
        type: 'attribute-search',
        isfrom: 'composer',
        rid: this.targetRid
      },
      autoFocus: 'input',
      width: '60vw',
      maxWidth: '60vw',
      maxHeight: 'calc(100vh - 150px)',
      height: 'calc(100vh - 150px)'
    });
  }
}
